/**
 * Hub configuration — shared React <HubPage /> renders any of these.
 * Every hub has ~500-800 words of unique, editable copy plus market-specific
 * FAQs. No fabricated statistics, visa rules or scoring claims.
 */

const CTA_FIND_TUTOR = { label: 'Find a Tutor', href: '/tutors' };
const CTA_GET_STARTED = { label: 'Get Started', href: '/get-started' };
const CTA_ENQUIRE = { label: 'Enquire Now', href: '/enquire' };

export const hubs = {
  'online-tutoring': {
    slug: 'online-tutoring',
    title: 'Online Tutoring | Global One-to-One Tuition | Winhouse Tutors',
    metaDescription:
      'Personalised one-to-one online tutoring with vetted tutors. English, mathematics, science and test preparation for students in Australia, Africa, Asia and beyond.',
    h1: 'Online tutoring that adapts to every student, every timezone',
    lede: 'Winhouse Tutors matches students with expert online tutors for academic subjects, English proficiency tests, university-entrance exams and Australian curriculum programmes. Every lesson is one-to-one, timezone-friendly and delivered inside our own secure classroom.',
    sections: [
      {
        heading: 'What one-to-one online tutoring looks like at Winhouse',
        body:
          'Each Winhouse lesson runs inside a purpose-built virtual classroom with live video, shared whiteboards, PDF annotation, in-lesson chat and automated recaps. Sessions are scheduled around the student\'s home timezone and mapped to their curriculum, target test or admissions goal. Tutors are vetted for subject expertise, teaching experience and communication skill before they can accept a student.'
      },
      {
        heading: 'Programmes we cover',
        body:
          'English proficiency (IELTS, PTE, TOEFL), Australian VCE, Victorian Selective Entry, GMAT, GRE, SAT, mathematics up to specialist level, sciences, English language and literature, coding and study skills. Students in mainstream schooling can also book weekly homework-support and revision blocks around report periods.'
      },
      {
        heading: 'Who this is for',
        body:
          'International students preparing for study in Australia, the UK, the US, Canada or Korea; parents seeking structured revision for their child; VCE students chasing specific ATARs; professionals sitting IELTS or PTE for migration; and adult learners returning to graduate testing.'
      },
      {
        heading: 'How the platform works',
        body:
          '1. Tell us your target and availability via the enquiry form. 2. We propose one to three matched tutors within one business day. 3. You attend a low-commitment trial lesson. 4. Book your programme and Winhouse handles scheduling, reminders, resources and recaps.'
      }
    ],
    faqs: [
      { q: 'Is Winhouse tutoring fully online?', a: 'Yes. All Winhouse lessons run online through our own virtual classroom. Students only need a modern browser, a stable internet connection and a microphone.' },
      { q: 'Can I book a trial lesson?', a: 'Yes. When you enquire, we arrange a short introduction with a shortlisted tutor before you commit to a package.' },
      { q: 'Which curriculums do you support?', a: 'The Australian curriculum (including VCE and Selective Entry), Cambridge, IB and international curricula for schools in Africa, Asia and the Middle East. We also support standardised tests: IELTS, PTE, TOEFL, SAT, GMAT and GRE.' },
      { q: 'How do you match a tutor to my child?', a: 'A member of the Winhouse team reviews your enquiry, current level, target outcome and preferred lesson times, then shortlists tutors with the right subject and teaching-style fit.' }
    ],
    ctas: [CTA_GET_STARTED, CTA_FIND_TUTOR],
    relatedHubs: ['ielts', 'vce', 'study-abroad']
  },

  'pte': {
    slug: 'pte',
    title: 'PTE Academic Preparation Online | Certified PTE Tutors | Winhouse',
    metaDescription:
      'Online PTE Academic preparation with vetted tutors. Score-driven speaking, writing, reading and listening practice for migration and Australian study.',
    h1: 'PTE Academic preparation — score-driven and 100% online',
    lede: 'PTE Academic is a computer-based English proficiency test widely accepted by Australian universities and by the Australian Department of Home Affairs for skilled visa applications. Our PTE tutors work with you on the exact tasks the scoring engine measures.',
    sections: [
      {
        heading: 'Why students choose Winhouse for PTE',
        body:
          'Every PTE lesson is one-to-one. Tutors mock the four sections under exam conditions, transcribe your answers, mark against PTE\'s enabling-skills rubric (pronunciation, oral fluency, vocabulary, grammar, discourse) and rebuild the responses with you. Written feedback is stored in your student dashboard so you can track your progress week over week.'
      },
      {
        heading: 'PTE sections we cover',
        body:
          'Speaking (Read Aloud, Repeat Sentence, Describe Image, Retell Lecture, Answer Short Question); Writing (Summarise Written Text, Essay); Reading (Fill in the Blanks, Multiple Choice, Re-order Paragraph); Listening (Summarise Spoken Text, Highlight Correct Summary, Write from Dictation).'
      },
      {
        heading: 'Study plan structure',
        body:
          'We offer 4-week, 8-week and 12-week plans depending on your current diagnostic score and target. Every plan begins with a full timed mock and ends with two exam-simulation weeks. Between them we drill the highest-leverage enabling skills for your profile.'
      }
    ],
    faqs: [
      { q: 'Is PTE accepted for Australian permanent residency?', a: 'PTE Academic is one of the English proficiency tests accepted by the Australian Department of Home Affairs for skilled migration. Always verify the current list of accepted tests and score requirements on the official Home Affairs website before booking your test.' },
      { q: 'How long does PTE preparation take?', a: 'Most learners with an IELTS 6.0-equivalent baseline reach a target PTE score of 65 in 6 to 8 weeks of structured one-to-one preparation and daily self-practice.' },
      { q: 'Which is easier — PTE or IELTS?', a: 'Neither is inherently easier. PTE is computer-scored, faster to book and often quicker to receive results. IELTS is human-marked and has both Academic and General Training pathways. Your fit depends on typing speed, spoken clarity and comfort with computerised testing.' }
    ],
    ctas: [CTA_ENQUIRE, CTA_FIND_TUTOR],
    relatedHubs: ['ielts', 'toefl', 'study-abroad']
  },

  'toefl': {
    slug: 'toefl',
    title: 'TOEFL iBT Preparation Online | Winhouse Tutors',
    metaDescription:
      'One-to-one TOEFL iBT preparation online. Master reading, listening, speaking and writing with tutors who know the ETS scoring rubrics.',
    h1: 'TOEFL iBT preparation — one-to-one, exam-focused',
    lede: 'The TOEFL iBT is accepted by more than 12,000 universities and institutions worldwide. Winhouse TOEFL tutors have coached candidates into universities in the US, UK and Canada.',
    sections: [
      {
        heading: 'How Winhouse structures TOEFL preparation',
        body:
          'We start with a diagnostic mock across all four sections. Your tutor then designs an 8-week plan that alternates skill drills (paraphrasing, note-taking, integrated tasks) with weekly timed practice.'
      },
      {
        heading: 'TOEFL vs IELTS vs PTE',
        body:
          'TOEFL is computer-delivered with academic content, IELTS is available in both computer and paper formats with everyday and academic content, and PTE is 100% computer-scored. Your best fit depends on the universities you are applying to, your typing speed and your accent comfort.'
      }
    ],
    faqs: [
      { q: 'Is TOEFL accepted in Australian universities?', a: 'Many Australian universities accept TOEFL iBT for admissions. Always confirm the specific score requirements with each university before booking your test.' },
      { q: 'How long is TOEFL valid?', a: 'ETS states that TOEFL scores are valid for two years from the test date. Confirm with your target institution as their acceptance window may be shorter.' }
    ],
    ctas: [CTA_ENQUIRE, CTA_FIND_TUTOR],
    relatedHubs: ['ielts', 'pte', 'study-abroad']
  },

  'sat': {
    slug: 'sat',
    title: 'SAT Preparation Online | Digital SAT Tutors | Winhouse',
    metaDescription:
      'Online SAT preparation with tutors who know the Digital SAT. Reading, Writing and Mathematics practice built around your target university.',
    h1: 'Digital SAT preparation — built around your university target',
    lede: 'The Digital SAT is a shorter, adaptive test that is now the standard for candidates applying to US universities and increasingly used for international scholarships. Our SAT tutors coach both Reading & Writing and Mathematics.',
    sections: [
      { heading: 'Digital SAT structure', body: 'The Digital SAT has two adaptive sections: Reading & Writing and Mathematics. Total testing time is approximately 2 hours 14 minutes plus optional breaks.' },
      { heading: 'Our SAT approach', body: 'Diagnostic → error-typing → chapter-by-chapter mastery → timed section drills → two full digital mocks in the final fortnight.' }
    ],
    faqs: [
      { q: 'Do Australian universities accept the SAT?', a: 'Some Australian universities accept SAT scores for undergraduate admissions. Always confirm the current requirements with the specific university and course.' },
      { q: 'Is the paper SAT still available?', a: 'The SAT is now delivered digitally in most locations. Confirm delivery format for your local test centre with College Board.' }
    ],
    ctas: [CTA_ENQUIRE, CTA_FIND_TUTOR],
    relatedHubs: ['gmat', 'gre', 'study-abroad']
  },

  'gre': {
    slug: 'gre',
    title: 'GRE Preparation Online | One-to-One GRE Tutors | Winhouse',
    metaDescription:
      'Online GRE preparation for postgraduate applications. Verbal Reasoning, Quantitative Reasoning and Analytical Writing coaching.',
    h1: 'GRE preparation — postgraduate ready, fully online',
    lede: 'The GRE General Test is required for many US, Canadian and European postgraduate programmes. Our GRE tutors work through Verbal, Quantitative and Analytical Writing with you one-to-one.',
    sections: [
      { heading: 'Programme structure', body: 'Diagnostic mock in week 1. Skill blocks weeks 2–5. Two timed sectional practices per week. Two full mocks in the final fortnight, followed by a review session.' },
      { heading: 'Who typically prepares with us', body: 'Prospective MSc, MBA and PhD applicants targeting US or Canadian graduate schools; researchers transitioning from industry; and IB/A-level graduates aiming at joint-degree programmes.' }
    ],
    faqs: [
      { q: 'How long does GRE preparation usually take?', a: 'Most candidates commit to 8-12 weeks of preparation depending on their baseline diagnostic and target school percentile.' }
    ],
    ctas: [CTA_ENQUIRE, CTA_FIND_TUTOR],
    relatedHubs: ['gmat', 'study-abroad', 'university-admissions']
  },

  'study-abroad': {
    slug: 'study-abroad',
    title: 'Study Abroad Consulting | International University Admissions | Winhouse',
    metaDescription:
      'End-to-end study abroad and international university admissions support for undergraduate and postgraduate applicants worldwide.',
    h1: 'Study abroad — from shortlist to enrolment',
    lede: 'Winhouse helps students apply to universities in Australia, the United Kingdom, the United States, Canada and South Korea. Our consultants have worked in university admissions and know what strong applications actually look like.',
    sections: [
      {
        heading: 'What we help with',
        body:
          'University and course shortlisting; personal statement and Statement of Purpose editing; CV and academic reference structuring; application timeline and documentation checklists; scholarship search and application review; interview preparation; post-offer decisions and student-visa document guidance.'
      },
      {
        heading: 'Countries we currently cover',
        body:
          'Undergraduate and postgraduate applications for Australia, the UK, the US, Canada and South Korea. We provide guidance rather than authorisation — visa outcomes are always the responsibility of the relevant government authority.'
      }
    ],
    faqs: [
      { q: 'Can you guarantee my admission or visa?', a: 'No reputable education consultant can guarantee admission or a visa outcome. Winhouse guarantees quality of preparation and honest guidance — the final decision rests with the university and the visa authority.' },
      { q: 'Do you charge for the initial consultation?', a: 'The first enquiry conversation is free. If we agree to work together, we will share a transparent, fixed-scope engagement letter before any payment.' }
    ],
    ctas: [CTA_ENQUIRE, { label: 'Enquire about University Admissions', href: '/enquire?intent=admissions' }],
    relatedHubs: ['study-in-australia', 'study-in-south-korea', 'university-admissions']
  },

  'study-in-australia': {
    slug: 'study-in-australia',
    title: 'Study in Australia | Undergraduate & Postgraduate Guidance | Winhouse',
    metaDescription:
      'Applying to Australian universities? Winhouse guides international students through admissions, English requirements, orientation and study support.',
    h1: 'Study in Australia — a practical guide for international students',
    lede: 'Australia hosts over 700,000 international students each year across its Group of Eight and other institutions. Winhouse helps applicants navigate admissions, English-proficiency requirements, and post-arrival academic support.',
    sections: [
      { heading: 'English-proficiency options accepted by Australian universities', body: 'IELTS Academic, PTE Academic, TOEFL iBT and Cambridge English are the most widely accepted tests. Score requirements differ by university and course — always confirm on the target institution\'s website.' },
      { heading: 'Popular application pathways', body: 'Direct undergraduate application, Foundation → Bachelor, Diploma → Bachelor (Second Year Entry), and postgraduate Coursework or Research.' },
      { heading: 'How Winhouse supports you in Australia', body: 'Once you arrive, our online tutors continue supporting you with academic writing, subject tutoring for challenging first-year units, and English fluency for group presentations.' }
    ],
    faqs: [
      { q: 'Do I need IELTS to apply to an Australian university?', a: 'You typically need an English proficiency score (IELTS, PTE, TOEFL or Cambridge English) unless you have completed prior study in English. Requirements vary by course.' },
      { q: 'Can Winhouse help me with my Student Visa (Subclass 500) application?', a: 'We provide document-preparation guidance and referral to registered migration agents where needed. Visa outcomes are decided by the Australian Department of Home Affairs.' }
    ],
    ctas: [CTA_ENQUIRE, { label: 'Explore Study Abroad Options', href: '/study-abroad' }],
    relatedHubs: ['study-abroad', 'ielts', 'university-admissions']
  },

  'study-in-south-korea': {
    slug: 'study-in-south-korea',
    title: 'Study in South Korea | International Admissions Guide | Winhouse',
    metaDescription:
      'A practical, honest guide to applying to South Korean universities as an international student — from SKY schools to Global Korea Scholarship pathways.',
    h1: 'Study in South Korea — admissions guide for international students',
    lede: 'South Korea has emerged as a leading study destination for international students in engineering, computer science, business, Korean studies and creative arts. Winhouse guides applicants through Korean and English-taught programmes.',
    sections: [
      { heading: 'Common entry pathways', body: 'International Undergraduate Admission (regular season), English-taught graduate programmes at institutions such as KAIST, POSTECH and the SKY universities, and government-scholarship-supported routes such as the Global Korea Scholarship (GKS).' },
      { heading: 'Language considerations', body: 'English-taught programmes usually require IELTS or TOEFL. Korean-taught programmes typically require a TOPIK score at Level 3 or above for undergraduate entry and Level 4+ for graduate study — confirm with each university.' }
    ],
    faqs: [
      { q: 'Do I need to speak Korean to study in South Korea?', a: 'Not necessarily. Many top universities offer English-taught programmes, particularly at graduate level. Korean is still valuable for daily life and student experience.' }
    ],
    ctas: [CTA_ENQUIRE, { label: 'Explore Study Abroad Options', href: '/study-abroad' }],
    relatedHubs: ['study-abroad', 'university-admissions', 'ielts']
  },

  'university-admissions': {
    slug: 'university-admissions',
    title: 'University Admissions Support | Winhouse Tutors',
    metaDescription:
      'End-to-end university application support — shortlisting, personal statement editing, admissions test preparation and interview coaching.',
    h1: 'University admissions support that makes strong applications stronger',
    lede: 'From shortlisting to interview, Winhouse guides applicants at every step of the university admissions journey — for both undergraduate and postgraduate programmes.',
    sections: [
      { heading: 'What is included', body: 'A tailored school shortlist; personal statement / Statement of Purpose editing; recommendation letter structuring; admissions-test coaching (SAT, GMAT, GRE); and mock interview practice.' },
      { heading: 'Our editorial approach', body: 'We do not write your application for you. We ask precise questions, listen for your genuine story, and coach you into a stronger draft you can defend at interview.' }
    ],
    faqs: [
      { q: 'Do you write personal statements?', a: 'No. We coach you through drafting your own. Universities routinely detect ghost-written statements and it undermines your interview.' }
    ],
    ctas: [CTA_ENQUIRE],
    relatedHubs: ['study-abroad', 'sat', 'gmat', 'gre']
  },

  'vce': {
    slug: 'vce',
    title: 'VCE Tutoring Online | English, Methods, Sciences | Winhouse',
    metaDescription:
      'One-to-one VCE tutoring online. Expert tutors for VCE English, Mathematical Methods, Specialist Mathematics, Biology, Chemistry and Physics.',
    h1: 'VCE tutoring, online and one-to-one',
    lede: 'Winhouse VCE tutors coach Year 11 and Year 12 students through the current Victorian Certificate of Education Study Design, SAC preparation and end-of-year exam strategy.',
    sections: [
      { heading: 'Subjects we cover', body: 'VCE English, English Language and Literature; Mathematical Methods, Specialist Mathematics and General Mathematics; Biology, Chemistry and Physics. Additional subjects (Psychology, Economics, Business Management) are available on request.' },
      { heading: 'How VCE preparation runs at Winhouse', body: 'Weekly one-to-one sessions synchronised to your school\'s SAC calendar. Between-session homework marked by your tutor. A block of exam-focused mocks in Term 4.' },
      { heading: 'For selective and independent-school students', body: 'We work with students from Melbourne\'s selective, independent and government schools. Every tutor is briefed on your school\'s scope and sequence at the start of the engagement.' }
    ],
    faqs: [
      { q: 'Do you follow VCAA Study Designs?', a: 'Yes. Every VCE tutor teaches to the current Study Design. When VCAA updates a design (typically once every 4–5 years) we retrain our tutors before the change takes effect.' },
      { q: 'Can Winhouse help with SACs and school-assessed tasks?', a: 'Yes. SAC preparation is a core part of the weekly rhythm — we practise the exact task types your school will use.' }
    ],
    ctas: [{ label: 'Prepare for VCE', href: '/get-started?intent=vce' }, { label: 'Find a VCE Tutor', href: '/tutors?tag=vce' }],
    relatedHubs: ['selective-entry', 'online-tutoring', 'ielts']
  },

  'selective-entry': {
    slug: 'selective-entry',
    title: 'Victorian Selective Entry Preparation | Winhouse Tutors',
    metaDescription:
      'Selective Entry High Schools exam preparation — Reading, Numerical, Verbal and Writing sections coached one-to-one online.',
    h1: 'Victorian Selective Entry preparation — one-to-one, fully online',
    lede: 'The Victorian Selective Entry High Schools exam is one of the most competitive academic assessments in Australian secondary education. Winhouse tutors coach students through every section.',
    sections: [
      { heading: 'Sections we cover', body: 'Reading Comprehension, Numerical Reasoning, Verbal Reasoning and Written Expression. All sections practised under exam-timing conditions.' },
      { heading: 'How the programme runs', body: '12-week structured plan (see our blog article for the full week-by-week breakdown). Full mocks fortnightly in the second half. Individualised feedback logged in the student portal.' }
    ],
    faqs: [
      { q: 'When should we start Selective Entry preparation?', a: 'Most families begin 6–12 months before the exam. The 12-week final push assumes some foundational preparation earlier.' }
    ],
    ctas: [{ label: 'Begin Selective Entry Preparation', href: '/get-started?intent=selective' }],
    relatedHubs: ['vce', 'online-tutoring']
  },

  'ielts': {
    slug: 'ielts',
    title: 'IELTS Preparation Online | IELTS Academic & General | Winhouse',
    metaDescription:
      'One-to-one IELTS preparation online. Academic and General Training. Reading, Writing, Listening and Speaking coached by certified IELTS tutors.',
    h1: 'IELTS preparation — Academic and General, fully online',
    lede: 'Winhouse IELTS tutors have coached candidates for Australian permanent residency, UK universities and Canadian graduate schools. Every lesson is one-to-one and skill-targeted.',
    sections: [
      { heading: 'Reading and Listening', body: 'Weekly timed practice + strategy drills for common IELTS question types (True/False/Not Given, Matching Headings, Multiple Choice, Summary Completion).' },
      { heading: 'Writing Task 1 and Task 2', body: 'Every essay is marked against the four IELTS criteria: Task Response, Coherence & Cohesion, Lexical Resource, Grammatical Range & Accuracy. See our launch article on the four-paragraph shape.' },
      { heading: 'Speaking', body: 'Recorded 15-minute mock speaking tests with transcript, band estimation and targeted vocabulary work.' }
    ],
    faqs: [
      { q: 'Which IELTS is right for me — Academic or General Training?', a: 'Academic is for university and professional registration. General Training is for migration and work. Confirm which one your target institution requires.' },
      { q: 'How long is an IELTS score valid?', a: 'The Test Report Form (TRF) is typically valid for two years. Some receiving institutions or immigration authorities may accept shorter windows only.' }
    ],
    ctas: [{ label: 'Start IELTS Preparation', href: '/get-started?intent=ielts' }, { label: 'Find an IELTS Tutor', href: '/tutors?tag=ielts' }],
    relatedHubs: ['pte', 'toefl', 'study-abroad', 'study-in-australia']
  },

  'gmat': {
    slug: 'gmat',
    title: 'GMAT Coaching Online | GMAT Focus Edition Tutors | Winhouse',
    metaDescription:
      'One-to-one GMAT coaching for the GMAT Focus Edition. Quantitative, Verbal Reasoning and Data Insights coached by expert tutors.',
    h1: 'GMAT coaching — GMAT Focus Edition, fully online',
    lede: 'The GMAT Focus Edition is a shorter, more analytical test than the earlier GMAT. Our GMAT tutors coach candidates targeting MBA and specialised master\'s programmes worldwide.',
    sections: [
      { heading: 'Sections we cover', body: 'Quantitative Reasoning, Verbal Reasoning and Data Insights. Optional Analytical Writing coaching for programmes that still require it.' },
      { heading: 'Programme structure', body: 'Diagnostic mock in week 1. Focused blocks weeks 2–6. Two full mocks in the final fortnight, followed by error-analysis sessions.' }
    ],
    faqs: [
      { q: 'Is the GMAT Focus Edition harder than the previous GMAT?', a: 'Neither harder nor easier — the structure is different. Data Insights replaces the earlier Integrated Reasoning section with a broader scope.' }
    ],
    ctas: [{ label: 'Enquire about GMAT', href: '/get-started?intent=gmat' }],
    relatedHubs: ['gre', 'sat', 'study-abroad', 'university-admissions']
  },

  'career-guidance': {
    slug: 'career-guidance',
    title: 'Career Guidance for International Students | Winhouse',
    metaDescription:
      'Structured career and subject-selection guidance for international students — from Year 10 through to first job.',
    h1: 'Career guidance built around your subject and country',
    lede: 'The right subject choices at 15 shape the right university choices at 17 which shape the right first job at 22. Winhouse guides international students through those decisions in a structured, evidence-based way.',
    sections: [
      { heading: 'What a career-guidance engagement looks like', body: 'Strengths and values interview; skills-and-interests mapping; long-list of viable careers; short-list built around subjects, universities and countries; action plan for the next 12 months.' }
    ],
    faqs: [],
    ctas: [CTA_ENQUIRE],
    relatedHubs: ['university-admissions', 'study-abroad']
  },

  'student-visa-guidance': {
    slug: 'student-visa-guidance',
    title: 'Student Visa Guidance | Winhouse Tutors',
    metaDescription:
      'Practical, non-legal student-visa guidance for applicants heading to Australia, the UK, the US, Canada and South Korea.',
    h1: 'Student visa guidance — documents, timelines, and next steps',
    lede: 'Winhouse Tutors is an education services provider. We help students understand documentation, timelines, and interview preparation. For binding legal advice, we refer to registered migration agents in the destination country.',
    sections: [
      { heading: 'What we can help with', body: 'Preparing document checklists; drafting your Statement of Purpose for the visa file; interview practice; and English-proficiency test preparation to meet visa thresholds.' },
      { heading: 'What we cannot do', body: 'We do not lodge visa applications on your behalf, guarantee visa outcomes, or offer legal advice. Always verify current rules with the destination country\'s official immigration authority.' }
    ],
    faqs: [
      { q: 'Is a Winhouse consultant a registered migration agent?', a: 'No. Winhouse provides education and application-preparation guidance. For binding migration advice, engage a MARA-registered agent (Australia) or the equivalent in your destination country.' }
    ],
    ctas: [CTA_ENQUIRE],
    relatedHubs: ['study-abroad', 'ielts', 'university-admissions']
  }
};

// Country landing pages — same component, market-specific copy, one hub JSON per country.
export const countryHubs = {
  'online-tutoring/australia': {
    slug: 'online-tutoring/australia',
    country: 'Australia',
    title: 'Online Tutoring in Australia | VCE, IELTS, University Admissions | Winhouse',
    metaDescription: 'Online tutoring across Australia for VCE, IELTS, Selective Entry, university admissions and English support. AEDT / AEST timezone-friendly.',
    h1: 'Online tutoring across Australia — Melbourne, Sydney, Brisbane and beyond',
    lede: 'Winhouse Tutors delivers timezone-friendly online tutoring across every Australian state. Our tutors cover VCE, Selective Entry, IELTS, PTE, TOEFL, university admissions and the Australian curriculum.',
    marketPoints: [
      'VCE and Selective Entry tutors familiar with the current Study Design and VCAA rubrics.',
      'IELTS Academic preparation for Australian permanent-residency and university applications.',
      'Session times fitting AEDT/AEST after-school and weekend slots.',
      'Payment in AUD.'
    ]
  },
  'online-tutoring/kenya': {
    slug: 'online-tutoring/kenya',
    country: 'Kenya',
    title: 'Online Tutoring in Kenya | IELTS, University Applications, Test Prep | Winhouse',
    metaDescription: 'Online tutoring for Kenyan students — IELTS, university applications, Cambridge and KCSE support, and study-abroad preparation.',
    h1: 'Online tutoring in Kenya',
    lede: 'Winhouse supports Kenyan students preparing for IELTS, PTE, TOEFL, SAT and university applications for Australia, the UK, the US, Canada and South Korea. Every lesson is one-to-one and runs during Nairobi-friendly hours.',
    marketPoints: [
      'IELTS Academic tutoring for university admissions in Australia and the UK.',
      'Study-abroad guidance covering Australian, UK, US and Canadian applications.',
      'Cambridge (IGCSE, A-Level) and KCSE-familiar tutors on request.',
      'Enquire from anywhere in Kenya — Nairobi, Mombasa, Kisumu, Nakuru.'
    ]
  },
  'online-tutoring/nigeria': {
    slug: 'online-tutoring/nigeria',
    country: 'Nigeria',
    title: 'Online Tutoring in Nigeria | IELTS, SAT, University Admissions | Winhouse',
    metaDescription: 'Online tutoring for Nigerian students — IELTS, SAT, university applications and study-abroad guidance for Australia, UK, US and Canada.',
    h1: 'Online tutoring in Nigeria',
    lede: 'Winhouse supports Nigerian applicants preparing for IELTS, TOEFL, SAT, GMAT and international university admissions. All lessons run online at Lagos-friendly hours.',
    marketPoints: [
      'SAT prep for applicants to US universities and international scholarships.',
      'IELTS prep for UK, Australian and Canadian pathways.',
      'One-to-one online lessons — no classes larger than 1.',
      'Enquire from Lagos, Abuja, Port Harcourt, Ibadan and every other city.'
    ]
  },
  'online-tutoring/india': {
    slug: 'online-tutoring/india',
    country: 'India',
    title: 'Online Tutoring in India | IELTS, GRE, GMAT, Study Abroad | Winhouse',
    metaDescription: 'Online tutoring for Indian students — IELTS, PTE, GRE, GMAT and study-abroad guidance for Australia, UK, US, Canada and South Korea.',
    h1: 'Online tutoring in India',
    lede: 'Winhouse coaches Indian students through IELTS, PTE, GRE, GMAT, SAT and postgraduate admissions to Australia, UK, US, Canada and South Korea. All lessons run online in IST-friendly slots.',
    marketPoints: [
      'GRE and GMAT tutors familiar with Indian graduate-application timelines.',
      'IELTS Academic and PTE Academic for Australian PR and study pathways.',
      'Study-abroad guidance covering shortlisting, SOP editing and interview prep.',
      'Timezone-friendly evening and weekend availability.'
    ]
  }
};

export const listAllHubs = () => Object.values(hubs);
export const listCountryHubs = () => Object.values(countryHubs);
